import Panel, { PanelAPIResponse, PanelTree } from "@/classes/panel";
import uniqBy from "lodash/uniqBy";
import sortBy from "lodash/sortBy";
import { DATA_API_SERVICE } from "@/appConfig";
import formatToAPIDate from "@/utils/formatToAPIDate";
import APICluster from "@/api/auth/cluster";
import APIStation from "@/api/auth/station";
import { ClusterTree } from "@/classes/cluster";
import API from "@/api/api";

/**
 * At the time of writing this panel API, I have made the assumption that panels
 * can be related to panels, and a cluster can belong to a panel. To create the
 * tree, I have to get the root node and then find all the child trees.
 */

async function create(panel: Panel) {
  const response = await API.post<PanelAPIResponse>(`${DATA_API_SERVICE}/panel`, {
    ...panel,
    id: undefined,
    createdAt: formatToAPIDate(panel.createdAt)
  });
  return new Panel({
    ...response.data,
    createdAt: new Date(response.data.createdAt)
  });
}

// TODO: The API response doesn't back any data about the panel
async function update(panel: Panel) {
  await API.put(`${DATA_API_SERVICE}/panel/${panel.id}`, panel);
  return new Panel(panel);
}

async function removePanel(panelId: string) {
  await API.delete(`${DATA_API_SERVICE}/panel/${panelId}`);
}

async function findPanelById(panelId: string) {
  const response = await API.get<PanelAPIResponse>(`${DATA_API_SERVICE}/panel/${panelId}`);
  return new Panel({
    ...response.data,
    createdAt: new Date(response.data.createdAt)
  });
}

async function getPanelsForProperty(propertyId: string) {
  const response = await API.get<PanelAPIResponse[]>(`${DATA_API_SERVICE}/panel/property/${propertyId}`);
  const panels = response.data.map(
    panelResponse =>
      new Panel({
        ...panelResponse,
        createdAt: new Date(panelResponse.createdAt)
      })
  );
  return sortBy(panels, panel => panel.name);
}

function findPanelInTree(panelId: string, fullTree: PanelTree): PanelTree | null {
  const tree = fullTree;
  if (tree.panel.id === panelId) {
    return fullTree;
  }
  for (const child of tree.panels) {
    const found = findPanelInTree(panelId, child);
    if (found) {
      return found;
    }
  }
  return null;
}

async function getPanelTree(panelId: string) {
  let loops = 0;
  let rootPanelInfo = await findPanelById(panelId);
  if (!rootPanelInfo) {
    throw new Error(`Panel ${panelId} not found`);
  }
  let parentId = rootPanelInfo.parentId;
  while (parentId) {
    loops++;
    if (loops > 2000) {
      throw new Error("Infinite loop detected.");
    }
    if (!rootPanelInfo.parentId) break;
    rootPanelInfo = await findPanelById(rootPanelInfo.parentId);
    if (!rootPanelInfo) break;
    parentId = rootPanelInfo.parentId;
  }
  if (!rootPanelInfo) {
    throw new Error(`Panel ${parentId} not found while getting parents of panel ${panelId}`);
  }

  // With the root panel, get the subpanel relationships
  const fullTree = await findSubpanelsForPanel(rootPanelInfo);

  // Return the panel in the tree
  return {
    root: fullTree,
    tree: findPanelInTree(panelId, fullTree)
  };
}

async function getPanelTreesForProperty(propertyId: string) {
  const panels = await getPanelsForProperty(propertyId);
  const trees = await Promise.all(
    panels.map(async panel => {
      const tree = await getPanelTree(panel.id);
      if (tree) {
        return tree.root;
      }
      return new PanelTree({
        panel
      });
    })
  );
  const uniqTrees = uniqBy(trees, tree => tree.panel.id);
  return sortBy(uniqTrees, tree => tree.panel.name);
}

async function getSubpanels(panel: Panel) {
  const panels = await getPanelsForProperty(panel.pKey);
  const filteredPanels = panels.filter(propertyPanel => propertyPanel.parentId === panel.id);
  return sortBy(filteredPanels, panel => panel.name);
}

async function findSubpanelsForPanel(panel: Panel) {
  const subpanels = await getSubpanels(panel);
  const children: PanelTree[] = [];
  if (subpanels.length) {
    for (const subpanel of subpanels) {
      children.push(await findSubpanelsForPanel(subpanel));
    }
  }

  // Get the stations and clusters for the panel
  const propertyClusters = await APICluster.getClustersForProperty(panel.pKey);
  const propertyStations = await APIStation.getStationsByPropertyId(panel.pKey);
  const panelClusters = propertyClusters.filter(cluster => cluster.panelKey === panel.id);
  const clusterTrees = panelClusters.map(cluster => {
    return new ClusterTree({
      cluster,
      stations: propertyStations.stations.filter(station => station.cluster.id === cluster.id)
    });
  });

  // Return the full panel tree
  return new PanelTree({
    panel,
    clusters: clusterTrees,
    panels: sortBy(children, child => child.panel.name)
  });
}

const APIPanel = {
  create,
  getPanelTree,
  getPanelsForProperty,
  getPanelTreesForProperty,
  removePanel,
  update
};

export default APIPanel;
